import React, { useState, useEffect } from 'react';
import Header from './components/Header';
import KPIGrid from './components/KPIGrid';
import FilterSection from './components/FilterSection';
import DataTable from './components/DataTable';
import DetailModal from './components/DetailModal';
import Dashboard from './components/Dashboard';
import StrategicMap from './components/StrategicMap'; // Importar o novo componente
import StrategicFlow from './components/StrategicFlow'; // Importar o novo componente StrategicFlow
import PerspectiveManagement from './components/PerspectiveManagement'; // Importar o novo componente de gestão de perspectivas
import ObjectiveManagement from "./components/ObjectiveManagement";
import IndicatorManagement from './components/IndicatorManagement'; // Importar o novo componente de gestão de indicadores
import PeriodManagement from './components/PeriodManagement'; // Importar o novo componente de gestão de períodos
import IndicatorValueManagement from './components/IndicatorValueManagement'; // Importar o novo componente de gestão de valores de indicadores
import BalancedScorecard from "./components/BalancedScorecard"; // Importar o novo componente BalancedScorecard
import LoginForm from './components/LoginForm';
import UserManagement from './components/UserManagement'; // Importar o novo componente UserManagement
import AccessLogs from './components/AccessLogs'; // Importar o novo componente AccessLogs
import { generateIndicatorsPPTX } from './components/pptxDashboardExport';
import { useBSCData } from './hooks/useBSCData';
import { useFilters } from './hooks/useFilters';
import { isAuthenticated, initAuth } from './services/auth';

// Importar o novo componente ObjectiveLinkManagement
import ObjectiveLinkManagement from './components/ObjectiveLinkManagement';

function App() {
  const [showDashboardView, setShowDashboardView] = useState(false);
  const [showStrategicMapView, setShowStrategicMapView] = useState(false); // Novo estado para o Mapa Estratégico
  const [showStrategicFlowView, setShowStrategicFlowView] = useState(false); // Novo estado para o Fluxo Estratégico
  const [showBalancedScorecard, setShowBalancedScorecard] = useState(false); // Novo estado para Balanced Scorecard
  const [showPerspectiveManagement, setShowPerspectiveManagement] = useState(false); // Novo estado para Gestão de Perspectivas
  const [showObjectiveManagement, setShowObjectiveManagement] = useState(false); // Novo estado para Gestão de Objetivos
  const [showIndicatorManagement, setShowIndicatorManagement] = useState(false); // Novo estado para Gestão de Indicadores
  const [showPeriodManagement, setShowPeriodManagement] = useState(false); // Novo estado para Gestão de Períodos
  const [showIndicatorValueManagement, setShowIndicatorValueManagement] = useState(false); // Novo estado para Gestão de Valores de Indicadores
  // Novo estado para ObjectiveLinkManagement
  const [showObjectiveLinkManagement, setShowObjectiveLinkManagement] = useState(false);
  // Novo estado para UserManagement
  const [showUserManagement, setShowUserManagement] = useState(false);
  // Novo estado para AccessLogs
  const [showAccessLogs, setShowAccessLogs] = useState(false);
  const [selectedIndicator, setSelectedIndicator] = useState(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false); // Estado para controlar logout
  const [currentView, setCurrentView] = useState('default'); // Estado para controlar a visualização atual

  // Verificação de autenticação
  useEffect(() => {
    const checkAuth = async () => {
      try {
        initAuth();
        setAuthenticated(isAuthenticated());
      } catch (error) {
        console.warn("Erro na inicialização da autenticação:", error.message);
        setAuthenticated(false);
      } finally {
        setAuthChecked(true);
      }
    };

    checkAuth();
  }, []);

  // Proteção global contra erros não tratados
  useEffect(() => {
    const handleGlobalError = (event) => {
      console.warn('Erro global capturado:', event.error);
      event.preventDefault();
    };

    const handleGlobalRejection = (event) => {
      console.warn('Promise rejeitada global capturada:', event.reason);
      event.preventDefault();
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleGlobalRejection);

    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleGlobalRejection);
    };
  }, []);

  // Sempre chamar useBSCData, mas passar o estado de autenticação e não executar durante logout
  const {
    indicators,
    setIndicators, // Necessário para atualizar o estado de indicadores
    loading,
    error,
    perspectives,
    indicatorTypes,
    kpiSummary,
    dataSource
  } = useBSCData(authenticated && authChecked && !isLoggingOut);

  const {
    searchTerm,
    setSearchTerm,
    selectedPerspective,
    setSelectedPerspective,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    showOnlyUnseen,
    setShowOnlyUnseen,
    dashboardFilters,
    togglePerspectiveFilter,
    toggleIndicatorFilter,
    selectAllFilters,
    clearAllFilters,
    setQuickPeriod,
    filteredData,
    uniqueIndicatorsForDashboard,
    getDateRange
  } = useFilters(indicators, perspectives, indicatorTypes);

  const openView = (viewName) => {
    setCurrentView(viewName);
    // Reset other states when opening a new view
    setShowDashboardView(false);
    setShowStrategicMapView(false);
    setShowStrategicFlowView(false); // Resetar o novo estado
    setShowBalancedScorecard(false); // Resetar o novo estado
    setShowPerspectiveManagement(false);
    setShowObjectiveManagement(false);
    setShowObjectiveLinkManagement(false); // Resetar o novo estado
    setShowIndicatorManagement(false);
    setShowPeriodManagement(false);
    setShowIndicatorValueManagement(false); // Resetar o novo estado
    setShowUserManagement(false); // Resetar o novo estado
    setShowAccessLogs(false); // Resetar o novo estado
    setSelectedIndicator(null);
  };

  const closeView = () => {
    setCurrentView('default');
  };

  const openDashboard = () => {
    console.log('Abrindo dashboard...', { indicators: indicators.length, showDashboardView });
    if (indicators.length === 0) {
      alert("Nenhum dados disponíveis para exibir no dashboard");
      return;
    }
    openView('dashboard');
  };

  const closeDashboard = () => {
    closeView();
  };

  const openStrategicMap = () => {
    console.log('Abrindo mapa estratégico...');
    openView('strategic-map');
  };

  const closeStrategicMap = () => {
    closeView();
  };

  const openStrategicFlow = () => {
    console.log('Abrindo fluxo estratégico...');
    openView('strategic-flow');
  };

  const closeStrategicFlow = () => {
    closeView();
  };

  const handleOpenBalancedScorecard = () => {
    console.log('Abrindo Balanced Scorecard...');
    openView('balanced-scorecard');
  };

  const closeBalancedScorecard = () => {
    closeView();
  };

  const openPerspectiveManagement = () => {
    console.log('Abrindo gestão de perspectivas...');
    openView('perspective-management');
  };

  const closePerspectiveManagement = () => {
    closeView();
  };

  const openObjectiveManagement = () => {
    console.log('Abrindo gestão de objetivos...');
    openView('objective-management');
  };

  const closeObjectiveManagement = () => {
    closeView();
  };

  const openIndicatorManagement = () => {
    console.log('Abrindo gestão de indicadores...');
    openView('indicator-management');
  };

  const closeIndicatorManagement = () => {
    closeView();
  };

  const openPeriodManagement = () => {
    console.log('🔧 Abrindo gestão de períodos...');
    openView('period-management');
  };

  const closePeriodManagement = () => {
    closeView();
  };

  const openIndicatorValueManagement = () => {
    console.log('Abrindo lançamento de valores...');
    setCurrentView('indicatorValueManagement');
    setShowIndicatorValueManagement(true);
    setShowDashboardView(false);
    setShowStrategicMapView(false);
    setShowStrategicFlowView(false);
    setShowBalancedScorecard(false); // Fechar Balanced Scorecard
    setShowPerspectiveManagement(false);
    setShowObjectiveManagement(false);
    setShowObjectiveLinkManagement(false); // Fechar ObjectiveLinkManagement
    setShowIndicatorManagement(false);
    setShowPeriodManagement(false);
  };

  const closeIndicatorValueManagement = () => {
    closeView();
  };

  // Nova função para abrir ObjectiveLinkManagement
  const openObjectiveLinkManagement = () => {
    console.log('Abrindo gestão de vínculos de objetivos...');
    openView('objective-link-management');
  };

  const closeObjectiveLinkManagement = () => {
    closeView();
  };

  // Nova função para abrir UserManagement
  const openUserManagement = () => {
    console.log('Abrindo gestão de usuários...');
    openView('user-management');
  };

  const closeUserManagement = () => {
    closeView();
  };

  // Nova função para abrir AccessLogs
  const openAccessLogs = () => {
    console.log('Abrindo logs de acesso...');
    openView('access-logs');
  };

  const closeAccessLogs = () => {
    closeView();
  };

  const openDetails = (indicator) => {
    setSelectedIndicator(indicator);
    // You can potentially open a modal or a detail view here
  };

  const [isGeneratingPptx, setIsGeneratingPptx] = useState(false);
  const handleIndicatorsPptx = async () => {
    if (!filteredData || filteredData.length === 0) {
      alert('Nenhum indicador disponível para exportação. Ajuste os filtros/período.');
      return;
    }
    try {
      setIsGeneratingPptx(true);
      await generateIndicatorsPPTX({ filteredData, indicators });
    } catch (e) {
      console.error('Erro ao gerar PPTX de indicadores:', e);
      alert('❌ Erro ao gerar o PPTX. Verifique o console para mais detalhes.');
    } finally {
      setIsGeneratingPptx(false);
    }
  };

  const closeDetails = () => {
    setSelectedIndicator(null);
    // Potentially close the modal or detail view
  };

  const updateIndicatorStatus = (indicatorId, newStatus) => {
    setIndicators(prevIndicators =>
      prevIndicators.map(indicator =>
        indicator.id === indicatorId
          ? { ...indicator, checked: newStatus ? 'T' : 'F' }
          : indicator
      )
    );

    // Também atualizar o selectedIndicator se for o mesmo
    if (selectedIndicator && selectedIndicator.id === indicatorId) {
      setSelectedIndicator(prev => ({ ...prev, checked: newStatus ? 'T' : 'F' }));
    }
  };

  const handleLogin = (loginResult) => {
    setAuthenticated(true);
  };

  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!authenticated) {
    return <LoginForm onLogin={handleLogin} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando dados...</p>
        </div>
      </div>
    );
  }

  // Apenas exibir tela de erro para erros críticos (API offline)
  if (error && (error.includes('API offline') || error.includes('indisponível') || error.includes('conectar'))) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md">
          <div className="text-6xl mb-4 text-red-500">🔌</div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">API Offline</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-red-700 text-sm">
              O servidor não está respondendo. Tente novamente em alguns minutos.
            </p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="App min-h-screen bg-gray-100">
      {/* Header sempre presente */}
      <Header
        indicators={indicators}
        kpiSummary={kpiSummary}
        dataSource={dataSource}
        openDashboard={openDashboard}
        openStrategicMap={openStrategicMap}
        openStrategicFlow={openStrategicFlow}
        onOpenBalancedScorecard={handleOpenBalancedScorecard} // Adicionado para abrir o Balanced Scorecard
        openPerspectiveManagement={openPerspectiveManagement}
        openObjectiveManagement={openObjectiveManagement}
        openIndicatorManagement={openIndicatorManagement}
        openPeriodManagement={openPeriodManagement} // Adicionado para abrir a gestão de períodos
        openIndicatorValueManagement={openIndicatorValueManagement} // Adicionado para abrir o lançamento de valores
        // Adicionado para abrir ObjectiveLinkManagement
        openObjectiveLinkManagement={openObjectiveLinkManagement}
        openUserManagement={openUserManagement} // Adicionado para abrir UserManagement
        openAccessLogs={openAccessLogs} // Adicionado para abrir AccessLogs
        onLogoutStart={setIsLoggingOut} // Passar função para gerenciar estado de logout
      />

      {currentView === 'dashboard' && (
        <Dashboard
          indicators={indicators}
          dashboardFilters={dashboardFilters}
          uniqueIndicatorsForDashboard={uniqueIndicatorsForDashboard}
          perspectives={perspectives}
          indicatorTypes={indicatorTypes}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedPerspective={selectedPerspective}
          setSelectedPerspective={setSelectedPerspective}
          startDate={startDate}
          setStartDate={setStartDate}

          endDate={endDate}
          setEndDate={setEndDate}
          togglePerspectiveFilter={togglePerspectiveFilter}
          toggleIndicatorFilter={toggleIndicatorFilter}
          selectAllFilters={selectAllFilters}
          clearAllFilters={clearAllFilters}
          setQuickPeriod={setQuickPeriod}
          getDateRange={getDateRange}
          closeDashboard={closeDashboard}
          openStrategicMap={openStrategicMap}
        />
      )}
      {currentView === 'strategic-map' && <StrategicMap closeStrategicMap={closeStrategicMap} />}
      {currentView === 'strategic-flow' && <StrategicFlow closeStrategicFlow={closeStrategicFlow} />}
      {currentView === 'balanced-scorecard' && <BalancedScorecard onClose={closeBalancedScorecard} />}
      {currentView === 'perspective-management' && <PerspectiveManagement onClose={closePerspectiveManagement} />}
      {currentView === 'objective-management' && <ObjectiveManagement onClose={closeObjectiveManagement} />}
      {currentView === 'indicator-management' && <IndicatorManagement onClose={closeIndicatorManagement} />}
      {currentView === 'period-management' && <PeriodManagement onClose={closePeriodManagement} />}
      {currentView === 'indicatorValueManagement' && <IndicatorValueManagement onClose={closeIndicatorValueManagement} />}
      {/* Novo render para ObjectiveLinkManagement */}
      {currentView === 'objective-link-management' && <ObjectiveLinkManagement onClose={closeObjectiveLinkManagement} />}
      {/* Novo render para UserManagement */}
      {currentView === 'user-management' && <UserManagement onClose={closeUserManagement} />}
      {/* Novo render para AccessLogs */}
      {currentView === 'access-logs' && <AccessLogs onClose={closeAccessLogs} />}


      {currentView === 'default' && (
        <div className="main-container fade-in">
          {indicators.length === 0 ? (
            <div className="min-h-[400px] flex items-center justify-center">
              <div className="text-center p-8 bg-white rounded-lg shadow-lg">
                <div className="text-blue-500 text-6xl mb-4">📊</div>
                <h2 className="text-xl font-semibold text-gray-800 mb-2">
                  Nenhum dado disponível
                </h2>
                <p className="text-gray-600 mb-4">
                  {dataSource === 'none' ?
                    'Não foi possível carregar os dados do sistema.' :
                    'Nenhum indicador foi encontrado para exibir.'
                  }
                </p>
                <button
                  onClick={() => window.location.reload()}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
                >
                  Recarregar Página
                </button>
              </div>
            </div>
          ) : (
            <>
              <KPIGrid kpiSummary={kpiSummary} />

              <FilterSection
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                selectedPerspective={selectedPerspective}
                setSelectedPerspective={setSelectedPerspective}
                perspectives={perspectives}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                showOnlyUnseen={showOnlyUnseen}
                setShowOnlyUnseen={setShowOnlyUnseen}
                getDateRange={getDateRange}
                setQuickPeriod={setQuickPeriod}
                clearAllFilters={clearAllFilters}
                filteredData={filteredData}
                indicators={indicators}
                onExportPptx={handleIndicatorsPptx}
                isGeneratingPptx={isGeneratingPptx}
              />

              <DataTable
                filteredData={filteredData}
                openDetails={openDetails}
                updateIndicatorStatus={updateIndicatorStatus}
              />
            </>
          )}
        </div>
      )}

      {selectedIndicator && currentView === 'default' && (
        <DetailModal
          selectedIndicator={selectedIndicator}
          indicators={indicators}
          closeDetails={closeDetails}
        />
      )}
    </div>
  );
}

export default App;